import React, { useState, useEffect } from 'react';
import { X, Check, X as XIcon } from 'lucide-react';
import { useAsistencia } from '@/hooks/useAsistencia';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { DiaEntrenamiento, AsistenciaJugador } from '@/types/asistencia';
import { JugadorDto } from '@/types/jugador';

interface ModalAsistenciaProps {
  dia: DiaEntrenamiento;
  jugadores: JugadorDto[];
  onCerrar: () => void;
  onGuardar: () => void;
}

/**
 * Modal para registrar la asistencia de jugadores a un entrenamiento
 */
export const ModalAsistencia: React.FC<ModalAsistenciaProps> = ({
  dia,
  jugadores,
  onCerrar,
  onGuardar
}) => {
  const [asistencias, setAsistencias] = useState<AsistenciaJugador[]>([]);
  const [guardando, setGuardando] = useState(false);
  
  const { 
    loading, 
    error, 
    registros, 
    obtenerRegistrosPorFecha, 
    guardarAsistencia, 
    prepararAsistenciaJugadores 
  } = useAsistencia();

  useEffect(() => {
    // Cargar registros existentes para esta fecha
    obtenerRegistrosPorFecha(dia.fecha);
  }, [dia.fecha]);

  useEffect(() => {
    // Preparar asistencias cuando se cargan los registros
    if (jugadores.length > 0) {
      const asistenciasPreparadas = prepararAsistenciaJugadores(jugadores, registros);
      setAsistencias(asistenciasPreparadas);
    }
  }, [jugadores, registros]);

  /**
   * Maneja el cambio de asistencia de un jugador
   */
  const handleCambiarAsistencia = (jugadorId: number, asistio: boolean) => {
    setAsistencias(prev => 
      prev.map(asistencia => 
        asistencia.jugadorId === jugadorId 
          ? { ...asistencia, asistio }
          : asistencia
      )
    );
  };

  /**
   * Maneja el guardado de asistencias
   */
  const handleGuardar = async () => {
    setGuardando(true);
    const exito = await guardarAsistencia(dia.fecha, asistencias);
    setGuardando(false);
    
    if (exito) {
      onGuardar();
    }
  };

  return (
    <Modal isOpen={true} onClose={onCerrar} size="lg">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 pb-4 border-b border-secondary-700">
        <h3 className="text-xl font-semibold text-secondary-50">
          Asistencia - {dia.dia} {dia.numero}
        </h3>
        <button
          onClick={onCerrar}
          className="p-2 rounded-lg text-secondary-400 hover:text-secondary-50 hover:bg-secondary-800 transition-all duration-200"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="max-h-96 overflow-y-auto mb-6">
        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-900/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {!loading && asistencias.length > 0 && (
          <div className="space-y-2">
            {asistencias.map((asistencia) => (
              <div 
                key={asistencia.jugadorId}
                className="flex items-center justify-between p-3 bg-secondary-800 rounded-lg border border-secondary-700"
              >
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-secondary-50 truncate block">
                    {asistencia.nombre} {asistencia.apellido}
                  </span>
                </div>
                
                <div className="flex space-x-1 ml-4">
                  <button
                    onClick={() => handleCambiarAsistencia(asistencia.jugadorId, true)}
                    className={`flex items-center justify-center w-8 h-8 rounded-md transition-all duration-200 ${
                      asistencia.asistio
                        ? 'bg-emerald-600 text-white shadow-md'
                        : 'bg-secondary-700 text-secondary-400 hover:bg-emerald-600/20 hover:text-emerald-400'
                    }`}
                    title="Asistió"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => handleCambiarAsistencia(asistencia.jugadorId, false)}
                    className={`flex items-center justify-center w-8 h-8 rounded-md transition-all duration-200 ${
                      !asistencia.asistio
                        ? 'bg-red-600 text-white shadow-md'
                        : 'bg-secondary-700 text-secondary-400 hover:bg-red-600/20 hover:text-red-400'
                    }`}
                    title="No asistió"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-700">
        <Button
          variant="secondary"
          size="sm"
          onClick={onCerrar}
          disabled={guardando}
        >
          Cancelar
        </Button>
        
        <Button
          variant="primary"
          size="sm"
          onClick={handleGuardar}
          disabled={guardando}
        >
          {guardando ? 'Guardando...' : 'Guardar'}
        </Button>
      </div>
    </Modal>
  );
};